import { Capacitor } from "@capacitor/core";
import { api } from "@/shared/api/client";

/**
 * 네이티브(안드로이드/iOS) 푸시 등록 — 백엔드 푸시 루프의 마지막 반쪽.
 *
 * 백엔드(`safety/services/notify/push.py`)는 이미 `NotificationPreference.devices`
 * 의 `{token, platform, label}` 목록으로 FCM/APNs 를 쏜다. 지금까지 그 목록에
 * 토큰을 넣는 주체가 없어 푸시가 끝단에서 끊겨 있었다. 이 모듈이 네이티브에서
 * FCM/APNs 토큰을 받아 그 목록에 등록해 루프를 닫는다.
 *
 * 웹(PWA 브라우저)에서는 `isNativePlatform()` 이 false 라 전부 no-op — 웹 번들은
 * 영향 없고, 실제 네이티브 플러그인 코드는 동적 import 라 웹에 섞여 들어가지 않는다.
 */

interface DeviceToken {
  token: string;
  platform: string;
  label?: string;
}

/**
 * 푸시 활성 여부 — **기본 꺼짐**.
 *
 * ⚠️ Android 에서 `PushNotifications.register()` 는 google-services.json 이 없으면
 * `IllegalStateException: Default FirebaseApp is not initialized` 를 **네이티브
 * 스레드에서** 던진다. 이건 JS try/catch 로 못 잡고 앱이 그대로 죽는다
 * (실측: 로그인 직후 FATAL EXCEPTION → 프로세스 종료).
 *
 * 그래서 자격이 갖춰졌을 때만 켠다. 켜는 조건:
 *   · Android — `android/app/google-services.json` 배치
 *   · iOS     — APNs 키 + Xcode Push Notifications capability
 * 둘 다 준비되면 빌드에 `VITE_ENABLE_PUSH=1` 을 준다.
 *
 * 꺼져 있어도 인앱 WebSocket 경보는 그대로 동작한다 — 잃는 것은 백그라운드
 * 알림뿐이고, 얻는 것은 "앱이 죽지 않는다"이다.
 */
// 2026-10-06(사용자 결정): 안드로이드는 기본으로 켠다 — google-services.json 이 리포에 있고(FCM 실기기 검증 09-11),
// 직접 설치 빌드(npm run release)에서 꺼져 있어 노트20·보드가 백그라운드 경보를 못 받았다. 웹 번들은 아이폰과 같으므로
// 플랫폼으로 가른다: 아이폰은 APNs 가 갖춰질 때까지 VITE_ENABLE_PUSH=1 일 때만. "0" 이면 어디서든 끈다(비상용).
const PUSH_FLAG = import.meta.env.VITE_ENABLE_PUSH;
const PUSH_ENABLED =
  PUSH_FLAG === "0" ? false : Capacitor.getPlatform() === "android" ? true : PUSH_FLAG === "1";

/**
 * 경보 알림 채널은 **네이티브가 만든다** — `android/…/AlertChannels.java`
 * (MainActivity.onCreate). 여기서 만들지 않는 이유:
 *
 * ⛔ Android 8+ 에서 소리·진동·헤드업은 「메시지」가 아니라 「채널」의 속성이고,
 *    Capacitor `createChannel` 은 소리를 `USAGE_NOTIFICATION` 으로만 싣는다 —
 *    진동 모드·알림 음량 0 이면 **가스 경보도 같이 조용해진다.** 경계·심각은
 *    알람 스트림(`USAGE_ALARM`)으로 울려야 하고, 그건 Java 에서만 지정할 수 있다.
 *    또 JS 는 로그인·권한 뒤에야 도는데 채널은 앱이 뜨는 순간 있어야 한다.
 *
 * 채널 id 는 서버 `push.py`·매니페스트 `default_notification_channel_id` 와
 * **글자 그대로** 같아야 한다 — 다르면 FCM 이 에러 없이 조용히 폴백 채널로 내려간다.
 *   · `gasm_alert_critical_v2` — 경계·심각, 커스텀 경보음(`res/raw/gasm_alarm.wav`)
 *   · `gasm_alert_v1`          — 주의, 기본 알림음
 */

export async function initNativePush(): Promise<void> {
  if (!Capacitor.isNativePlatform()) return;
  if (!PUSH_ENABLED) return;

  const { PushNotifications } = await import("@capacitor/push-notifications");

  let perm = await PushNotifications.checkPermissions();
  if (perm.receive === "prompt" || perm.receive === "prompt-with-rationale") {
    perm = await PushNotifications.requestPermissions();
  }
  if (perm.receive !== "granted") return;

  await PushNotifications.removeAllListeners();

  // FCM(안드로이드)·APNs(iOS) 토큰 수신 → 서버 등록.
  PushNotifications.addListener("registration", (token) => {
    void registerDeviceToken(token.value, Capacitor.getPlatform());
  });
  PushNotifications.addListener("registrationError", () => {
    /* 토큰 등록 실패로 앱을 막지 않는다 — 인앱 WebSocket 경보는 계속 동작 */
  });
  // 알림 탭 → 경보 화면으로. (해시 라우터가 아니어도 안전한 최소 이동)
  PushNotifications.addListener("pushNotificationActionPerformed", () => {
    try {
      if (location.pathname !== "/alerts") location.assign("/alerts");
    } catch {
      /* noop */
    }
  });

  try {
    await PushNotifications.register();
  } catch {
    /* JS 로 전달되는 실패만 여기서 삼킨다. 네이티브 FATAL 은 위 게이트로 막는다. */
  }
}

/** 현재 devices 목록을 읽어 이 토큰을 dedupe 추가한 뒤 PUT. push_enabled 도 켠다. */
async function registerDeviceToken(token: string, platform: string): Promise<void> {
  try {
    const pref = (await api.get<{ devices?: DeviceToken[] }>("/notifications/preferences/")).data;
    const devices: DeviceToken[] = Array.isArray(pref.devices) ? pref.devices : [];
    if (devices.some((d) => d?.token === token)) return; // 이미 등록됨
    const next = [...devices, { token, platform, label: `${platform} app` }];
    await api.put("/notifications/preferences/me/", { devices: next, push_enabled: true });
  } catch {
    /* 서버 등록 실패는 조용히 넘긴다 — 다음 실행 시 재시도된다 */
  }
}
